type RecipeGraphResponse = {
  root: string; // id of center recipe
  nodes: {
    id: string;
    name: string;
    description: string;
    family: "tabular" | "anomaly" | "forecast" | null;
    tags: string[];
  }[];
  edges: {
    from_recipe_id: string;
    to_recipe_id: string;
    relation_type: "prerequisite" | "followup" | "see_also";
    note: string | null;
  }[];
};

type Props = { graph: RecipeGraphResponse };

const MAX_NODES_PER_SIDE = 6;
const NODE_WIDTH = 130;
const NODE_HEIGHT = 36;
const CENTER_X = 330;
const CENTER_Y = 130;
const SIDE_OFFSET = 220;
const VERTICAL_SPACING = 46;

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength - 1)}…`;
}

export function RelationsGraph({ graph }: Props) {
  const { root, nodes, edges } = graph;

  if (edges.length === 0) {
    return (
      <svg
        viewBox="0 0 660 260"
        style={{ maxHeight: "260px", width: "100%" }}
        className="border border-slate-200 dark:border-slate-700 rounded-lg"
        role="img"
        aria-label="Recipe relations graph - no relations"
      >
        <title>No relations</title>
        <text
          x="330"
          y="130"
          textAnchor="middle"
          className="text-sm italic fill-slate-500 dark:fill-slate-400"
        >
          No relations yet
        </text>
      </svg>
    );
  }

  // Find center node
  const centerNode = nodes.find((n) => n.id === root);
  if (!centerNode) return null;

  // Group neighbors by relation type as-is from API (no inversion).
  // API is directional — render exactly what was stored.
  // Deduplicate: each node shown once (first edge wins).
  const placed = new Set<string>();
  const prerequisites: string[] = [];
  const followups: string[] = [];
  const seeAlso: string[] = [];

  for (const edge of edges) {
    const neighborId = edge.from_recipe_id === root ? edge.to_recipe_id : edge.from_recipe_id;
    if (placed.has(neighborId)) continue;
    placed.add(neighborId);

    if (edge.relation_type === "prerequisite") {
      prerequisites.push(neighborId);
    } else if (edge.relation_type === "followup") {
      followups.push(neighborId);
    } else {
      seeAlso.push(neighborId);
    }
  }

  // Truncate lists and compute overflow counts
  const prereqOverflow = Math.max(0, prerequisites.length - MAX_NODES_PER_SIDE);
  const followupOverflow = Math.max(0, followups.length - MAX_NODES_PER_SIDE);
  const seeAlsoOverflow = Math.max(0, seeAlso.length - MAX_NODES_PER_SIDE);

  const visiblePrereqs = prerequisites.slice(0, MAX_NODES_PER_SIDE);
  const visibleFollowups = followups.slice(0, MAX_NODES_PER_SIDE);
  const visibleSeeAlso = seeAlso.slice(0, MAX_NODES_PER_SIDE);

  // Compute positions for each side
  const leftNodes = visiblePrereqs.map((id, i) => {
    const node = nodes.find((n) => n.id === id);
    return {
      id,
      name: node?.name || "",
      x: CENTER_X - SIDE_OFFSET,
      y: CENTER_Y + (i - visiblePrereqs.length / 2 + 0.5) * VERTICAL_SPACING,
      color: "#7C8AA4", // navy-300
    };
  });

  const rightNodes = visibleFollowups.map((id, i) => {
    const node = nodes.find((n) => n.id === id);
    return {
      id,
      name: node?.name || "",
      x: CENTER_X + SIDE_OFFSET,
      y: CENTER_Y + (i - visibleFollowups.length / 2 + 0.5) * VERTICAL_SPACING,
      color: "#2DAA9E", // teal
    };
  });

  const topNodes = visibleSeeAlso.map((id, i) => {
    const node = nodes.find((n) => n.id === id);
    return {
      id,
      name: node?.name || "",
      x: CENTER_X + (i - visibleSeeAlso.length / 2 + 0.5) * (NODE_WIDTH + 10),
      y: 40,
      color: "#E0A458", // saffron
    };
  });

  // Calculate dynamic viewBox height
  const maxVerticalNodes = Math.max(leftNodes.length, rightNodes.length);
  const minHeight = 260;
  const dynamicHeight = Math.max(
    minHeight,
    CENTER_Y + (maxVerticalNodes / 2) * VERTICAL_SPACING + 60,
  );

  return (
    <svg
      viewBox={`0 0 660 ${dynamicHeight}`}
      style={{ maxHeight: "260px", width: "100%" }}
      className="border border-slate-200 dark:border-slate-700 rounded-lg"
      role="img"
      aria-label="Recipe relations neighborhood graph"
    >
      {/* Lines from neighbors to center */}
      {leftNodes.map((node) => (
        <line
          key={`line-left-${node.id}`}
          x1={node.x + NODE_WIDTH}
          y1={node.y + NODE_HEIGHT / 2}
          x2={CENTER_X - NODE_WIDTH / 2}
          y2={CENTER_Y + NODE_HEIGHT / 2}
          stroke={node.color}
          strokeWidth="2"
        />
      ))}
      {rightNodes.map((node) => (
        <line
          key={`line-right-${node.id}`}
          x1={CENTER_X + NODE_WIDTH / 2}
          y1={CENTER_Y + NODE_HEIGHT / 2}
          x2={node.x}
          y2={node.y + NODE_HEIGHT / 2}
          stroke={node.color}
          strokeWidth="2"
        />
      ))}
      {topNodes.map((node) => (
        <line
          key={`line-top-${node.id}`}
          x1={node.x + NODE_WIDTH / 2}
          y1={node.y + NODE_HEIGHT}
          x2={CENTER_X}
          y2={CENTER_Y}
          stroke={node.color}
          strokeWidth="2"
        />
      ))}

      {/* Center node */}
      <rect
        x={CENTER_X - NODE_WIDTH / 2}
        y={CENTER_Y}
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
        className="fill-navy-700 dark:fill-zinc-700"
        rx="6"
      />
      <text
        x={CENTER_X}
        y={CENTER_Y + NODE_HEIGHT / 2 + 5}
        textAnchor="middle"
        className="text-sm font-medium fill-white dark:fill-zinc-100"
      >
        {truncate(centerNode.name, 16)}
      </text>

      {/* Left neighbors (prerequisites) */}
      {leftNodes.map((node) => (
        <a key={`left-${node.id}`} href={`/recipes/${node.id}`}>
          <rect
            x={node.x}
            y={node.y}
            width={NODE_WIDTH}
            height={NODE_HEIGHT}
            fill="white"
            stroke={node.color}
            strokeWidth="2"
            rx="6"
            className="dark:fill-slate-800 hover:opacity-80 transition-opacity"
          />
          <text
            x={node.x + NODE_WIDTH / 2}
            y={node.y + NODE_HEIGHT / 2 + 5}
            textAnchor="middle"
            className="text-sm fill-slate-900 dark:fill-slate-100"
          >
            {truncate(node.name, 16)}
          </text>
        </a>
      ))}
      {prereqOverflow > 0 && (
        <text
          x={CENTER_X - SIDE_OFFSET + NODE_WIDTH / 2}
          y={CENTER_Y + (visiblePrereqs.length / 2 + 0.5) * VERTICAL_SPACING + 20}
          textAnchor="middle"
          className="text-xs fill-slate-500 dark:fill-slate-400"
        >
          +{prereqOverflow} more
        </text>
      )}

      {/* Right neighbors (followups) */}
      {rightNodes.map((node) => (
        <a key={`right-${node.id}`} href={`/recipes/${node.id}`}>
          <rect
            x={node.x}
            y={node.y}
            width={NODE_WIDTH}
            height={NODE_HEIGHT}
            fill="white"
            stroke={node.color}
            strokeWidth="2"
            rx="6"
            className="dark:fill-slate-800 hover:opacity-80 transition-opacity"
          />
          <text
            x={node.x + NODE_WIDTH / 2}
            y={node.y + NODE_HEIGHT / 2 + 5}
            textAnchor="middle"
            className="text-sm fill-slate-900 dark:fill-slate-100"
          >
            {truncate(node.name, 16)}
          </text>
        </a>
      ))}
      {followupOverflow > 0 && (
        <text
          x={CENTER_X + SIDE_OFFSET + NODE_WIDTH / 2}
          y={CENTER_Y + (visibleFollowups.length / 2 + 0.5) * VERTICAL_SPACING + 20}
          textAnchor="middle"
          className="text-xs fill-slate-500 dark:fill-slate-400"
        >
          +{followupOverflow} more
        </text>
      )}

      {/* Top neighbors (see_also) */}
      {topNodes.map((node) => (
        <a key={`top-${node.id}`} href={`/recipes/${node.id}`}>
          <rect
            x={node.x}
            y={node.y}
            width={NODE_WIDTH}
            height={NODE_HEIGHT}
            fill="white"
            stroke={node.color}
            strokeWidth="2"
            rx="6"
            className="dark:fill-slate-800 hover:opacity-80 transition-opacity"
          />
          <text
            x={node.x + NODE_WIDTH / 2}
            y={node.y + NODE_HEIGHT / 2 + 5}
            textAnchor="middle"
            className="text-sm fill-slate-900 dark:fill-slate-100"
          >
            {truncate(node.name, 16)}
          </text>
        </a>
      ))}
      {seeAlsoOverflow > 0 && (
        <text
          x={CENTER_X}
          y={20}
          textAnchor="middle"
          className="text-xs fill-slate-500 dark:fill-slate-400"
        >
          +{seeAlsoOverflow} more
        </text>
      )}
    </svg>
  );
}
